import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Text,
  UnstyledButton,
  Group,
  Stack,
  Button,
  ScrollArea,
  Flex,
  Box,
  SimpleGrid,
} from '@mantine/core';
import {
  IconSearch,
  IconCircle,
  IconCircleCheckFilled,
} from '@tabler/icons-react';
import { primaryColor } from '../../constants/theme';

type LanguagesModalProps = {
  opened: boolean;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  onSave: () => void;
  onCancel: () => void;
};

const getLanguageFlag = (languageName: string) => {
  switch (languageName.toLowerCase()) {
    case 'english':
    case 'english (uk)':
      return '🇬🇧';
    case 'french':
    case 'français':
      return '🇫🇷';
    case 'german':
    case 'deutsch':
      return '🇩🇪';
    case 'spanish':
    case 'español':
      return '🇪🇸';
    case 'chinese':
    case '简体中文':
      return '🇨🇳';
    case 'japanese':
    case '日本語':
      return '🇯🇵';
    case 'korean':
    case '한국어':
      return '🇰🇷';
    case 'russian':
      return '🇷🇺';
    case 'portuguese':
    case 'português':
      return '🇵🇹';
    case 'italian':
    case 'italiano':
      return '🇮🇹';
    case 'dutch':
      return '🇳🇱';
    case 'arabic':
      return '🇸🇦';
    case 'hindi':
      return '🇮🇳';
    case 'bengali':
      return '🇧🇩';
    case 'punjabi':
      return '🇮🇳';
    case 'javanese':
      return '🇮🇩';
    case 'vietnamese':
      return '🇻🇳';
    case 'urdu':
      return '🇵🇰';
    case 'persian':
      return '🇮🇷';
    case 'turkish':
      return '🇹🇷';
    default:
      return '🌐';
  }
};

export const LanguagesModal: React.FC<LanguagesModalProps> = ({
  opened,
  options,
  value,
  onChange,
  onSave,
  onCancel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Reset search query when modal opens
  useEffect(() => {
    if (opened) {
      setSearchQuery('');
    }
  }, [opened]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const toggleLanguage = (val: string) => {
    const newVal = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val];
    onChange(newVal);
  };

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        <Text fw={600} size="xl">
          Select Language
        </Text>
      }
      size="lg"
      radius="md"
      centered
      padding="xl"
      data-testid="languages-modal"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search languages..."
          leftSection={<IconSearch size={20} color="gray" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          radius="md"
          size="lg"
          styles={{ input: { fontSize: 'var(--mantine-font-size-md)' } }}
        />

        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm">
          {searchQuery ? 'Search Results' : 'Languages'}
        </Text>

        <ScrollArea h={350} data-testid="languages-options-list">
          {filteredOptions.length > 0 ? (
            <SimpleGrid
              cols={{ base: 1, sm: 2 }}
              spacing="sm"
              pr={{ base: 0, sm: 'sm' }}
            >
              {filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <UnstyledButton
                    key={option.value}
                    onClick={() => toggleLanguage(option.value)}
                    style={{
                      border: `1px solid ${
                        isSelected ? '#14B885' : 'var(--mantine-color-gray-3)'
                      }`,
                      borderRadius: 'var(--mantine-radius-md)',
                      padding: '12px 16px',
                      backgroundColor: isSelected ? '#E6F9F0' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Group gap="md">
                        <Box
                          bg="gray.1"
                          style={{
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 28,
                          }}
                        >
                          <Text size="md" lh={1}>
                            {getLanguageFlag(option.label)}
                          </Text>
                        </Box>
                        <Text
                          fw={500}
                          size="md"
                          c={isSelected ? 'dark.9' : 'dark.7'}
                        >
                          {option.label}
                        </Text>
                      </Group>
                      {isSelected ? (
                        <IconCircleCheckFilled size={24} color="#14B885" />
                      ) : (
                        <IconCircle
                          size={24}
                          color="var(--mantine-color-gray-4)"
                        />
                      )}
                    </Flex>
                  </UnstyledButton>
                );
              })}
            </SimpleGrid>
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              No languages found
            </Text>
          )}
        </ScrollArea>

        <Group grow gap="md" mt="md">
          <Button
            onClick={onSave}
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="save-languages-button"
          >
            Save
          </Button>
          <Button
            onClick={onCancel}
            variant="outline"
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="cancel-languages-button"
            styles={{
              root: {
                borderWidth: 1,
              },
            }}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
